<script setup>
import { ref, onMounted, nextTick } from 'vue';
import axios from "@/plugins/axios";
import SchemaTable from '@/pages/explore/SchemaTable.vue';
import { useToast } from '@/composables/use-toast';
import { useRouter } from 'vue-router';
import RFPConfirmation from '@/pages/RFPConfirmation.vue';
const router = useRouter();
const { addToast } = useToast();

const rfps = ref([]);
const isLoading = ref(false);
const showSchemaModal = ref(false);
const selectedRFP = ref(null);
const showAcceptConfirmation = ref(false);
const isAcceptLoading = ref(false);
const isRejectLoading = ref(false);

const getRequests = async (isApplicant) => {
    isLoading.value = true;
    try {
        const response = await axios.get('api/rfp-requests', {
            params: {
                is_applicant: isApplicant
            }
        });
        const rfpData = response.data.sort((a, b) => {
            return new Date(b.created_at) - new Date(a.created_at);
        });
        isLoading.value = false;
        return rfpData;
    } catch (error) {
        console.error('Failed to load RFP requests:', error);
        addToast('Error loading RFP requests. Try again.', 'error');
        isLoading.value = false;
        return [];
    }
};

const convertDateToHuman = (date) => {
    const dateObject = new Date(date);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return dateObject.toLocaleDateString('en-US', options);
}

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const showSchema = (rfp) => {
    selectedRFP.value = rfp;
    showSchemaModal.value = true;
};

const acceptRFP = async (rfp) => {
    isAcceptLoading.value = true;
    try {
        const endpoint = rfp.status === "pending_admin" ? 'approve' : 'accept'
        const response = await axios.post(`/api/rfp-requests/${endpoint}/${rfp.id}`)
        if(response.status === 200 || response.status === 201) {
           if(endpoint === 'accept') {
               selectedRFP.value = rfp;
               showAcceptConfirmation.value = true;
           } 
           rfp.status = 'accepted';
        }
    } catch (error) {
        console.error('Failed to process RFP request:', error);
        addToast('Error processing request. Try again.', 'error');
    } finally {
        isAcceptLoading.value = false;
    }
}

const rejectRFP = async (rfp) => {
    isRejectLoading.value = true;
   
    try {
        const response = await axios.post(`/api/rfp-requests/reject/${rfp.id}`)
        if(response.status === 200 || response.status === 201) {
            rfp.status = 'rejected';
        }
    } catch (error) {
        console.error('Failed to reject RFP request:', error);
        addToast('Error rejecting request. Try again.', 'error');
    } finally {
        isRejectLoading.value = false;
    }
}

const goToRFP = async (id) => {
    await nextTick();
    
    const element = document.getElementById(id);
    if (element) {
        element.classList.add('highlight-rfp');
        
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
        });
        
        setTimeout(() => {
            element.classList.remove('highlight-rfp');
        }, 3000);
    } else {
        addToast('RFP não encontrado', 'error');
    }
}

onMounted(async () => {
    rfps.value = await getRequests(true);
    if(router.currentRoute.value.query?.id) {
        goToRFP(router.currentRoute.value.query.id);
    }
});

</script>

<template>
    <div>
        <RFPConfirmation 
            v-if="showAcceptConfirmation" 
            :show-navbar="false"
            :title="'RFP Accepted Successfully!'"
            :message="'You will be redirected in'"
            :button-text="'Redirect to RFP'"
            :redirect-path="`/item-detail/${selectedRFP?.listing?.id}`"
            :show-countdown="true"
            :countdown-duration="5"
        />
        
        <div v-else class="space-y-6">
            <div v-if="isLoading" class="flex justify-center items-center min-h-[500px]">
                <div class="loading-spinner"></div>
            </div>
            
            <div v-else-if="rfps.length === 0" class="text-center flex items-center justify-center min-h-[500px]">
                <p class="text-white/80">No RFP requests found.</p>
            </div>
            
            <div v-else v-for="rfp in rfps" :key="rfp.id" class="bg-echoes-background-gray border border-echoes-border-light rounded-[13px] overflow-hidden mb-6" :id="rfp.id"
                :class="{
                    'animate-pulse opacity-75': isAcceptLoading || isRejectLoading
                }"
            >
                <div class="px-3 p-2 bg-echoes-background-gray md:bg-echoes-border-light border-b border-echoes-border-gray flex items-center justify-between">
                    <div class="flex items-center gap-4 truncate w-2/3">
                        <h3 class="text-base font-semibold text-white md:hidden">{{ rfp.listing.name }}</h3>
                        <h3 class="text-base font-semibold text-white hidden md:block">Review Request</h3>
                    </div>
                    <div v-if="rfp.status === 'pending_admin' || rfp.status === 'pending_applicant'" class="flex items-center gap-1.5">
                        <button 
                            @click="rejectRFP(rfp)" 
                            :disabled="isAcceptLoading || isRejectLoading || rfp.status === 'accepted'"
                            class="inline-flex items-center justify-center h-7 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ButtonLoading v-if="isRejectLoading" size="xs" text="" />
                            <span v-else class="flex items-center gap-4">
                                <i class="mdi mdi-close text-sm"></i>
                                <span class="hidden md:block">Reject</span>
                            </span>
                        </button>
                        <button 
                            @click="acceptRFP(rfp)" 
                            :disabled="isAcceptLoading || isRejectLoading || rfp.status === 'rejected'"
                            class="inline-flex items-center justify-center h-7 px-4 py-2 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ButtonLoading v-if="isAcceptLoading" size="xs" text="" />
                            <span v-else class="flex items-center gap-2">
                                <i class="mdi mdi-check text-sm"></i>
                                <span class="hidden md:block">Accept</span>
                            </span>
                        </button>
                    </div>
                    <div v-else>
                       <p v-if="rfp.status === 'accepted'" class="flex items-center gap-1 text-green-500 text-sm"><i class="mdi mdi-check text-base"></i>Accepted</p>
                       <p v-else-if="rfp.status === 'rejected'" class="flex items-center gap-1 text-red-500 text-sm"><i class="mdi mdi-close text-base"></i>Rejected</p>
                    </div>
                </div>

                <div>
                    <div class="flex divide-x divide-echoes-border-light">
                        <!-- Listing Info -->
                        <div class="md:w-[60%] p-3 items-center gap-4 hidden md:flex">
                            <div class="md:w-36 md:h-full h-24 w-24 bg-echoes-background-black border border-echoes-border-light rounded-[10px] overflow-hidden">
                                <img v-if="rfp.listing.preview_image" :src="rfp.listing.preview_image" alt="Listing" class="w-full h-full object-cover">
                                <div v-else class="w-full h-full flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="none" viewBox="0 0 24 24" class="w-10 h-10 text-gray-600">
                                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                            d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12" />
                                    </svg>
                                </div>
                            </div>
                            <div class="flex-1 min-w-0 flex flex-col gap-6">
                                <div class="flex items-start justify-between">
                                    <div class="flex flex-col w-full">
                                        <h2 class="font-semibold text-white truncate">{{ rfp.listing.name }}</h2>
                                        <p class="mt-0.5 text-sm text-white/80 line-clamp-2">{{ rfp.listing.short_description }}</p>
                                    </div>
                                </div>
                                

                                <div class="flex-1 flex flex-col justify-center">
                                    <div class="grid grid-cols-3 gap-x-3 gap-y-1.5 text-sm">
                                        <div class="space-y-0.5">
                                            <dl>
                                                <dt class="text-white/80 text-xs">Applicant</dt>
                                                <dd class="text-sm font-medium text-white truncate">{{ rfp.listing.applicant.username }}</dd>
                                            </dl>
                                        </div>
                                        <div class="space-y-0.5">
                                            <dl>
                                                <dt class="text-white/80 text-xs">Price</dt>
                                                <dd class="font-medium text-white">{{ rfp.listing.price }}</dd>
                                            </dl>
                                        </div>
                                        <div class="space-y-0.5">
                                            <dl>
                                                <dt class="text-white/80 text-xs">Created At</dt>
                                                <dd class="font-medium text-white">{{ convertDateToHuman(rfp.listing.created_at) }}</dd>
                                            </dl>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Dataset Info -->
                        <div class="flex-1 p-3 gap-4 flex flex-col">
                            <div class="flex items-center">
                                <div class="flex-1 min-w-0">
                                    <h3 class="font-semibold text-white truncate">{{ rfp.dataset.name }}</h3>
                                    <p class="text-sm text-white/80 truncate mt-0.5">{{ rfp.dataset.file_name }}</p>
                                </div>
                            </div>

                            <div class="flex-1 flex flex-col justify-center">
                                <div class="grid grid-cols-3 text-sm ">
                                    <div class="space-y-0.5">
                                        <dl>
                                            <dt class="text-white/80 text-xs">Owner</dt>
                                            <dd class="text-sm  text-white truncate">{{ rfp.dataset.seller.user.username }}</dd>
                                        </dl>
                                    </div>
                                    <div class="space-y-0.5">
                                        <dl>
                                            <dt class="text-white/80 text-xs">File Size</dt>
                                            <dd class=" text-white">{{ formatFileSize(rfp.dataset.file_size) }}</dd>
                                        </dl>
                                    </div>
                                  
                                    <div class="space-y-0.5">
                                        <dl>
                                            <dt class="text-white/80 text-xs">Created At</dt>
                                            <dd class="text-white">{{ convertDateToHuman(rfp.dataset.created_at) }}</dd>
                                        </dl>
                                    </div>
                                </div>

                            </div>
                            <button 
                                @click="showSchema(rfp)"
                                class="inline-flex items-center justify-center gap-2 px-3.5 py-1.5 text-sm font-medium bg-echoes-primary hover:bg-echoes-primary/50 rounded transition-colors flex-shrink-0">
                                <i class="mdi mdi-table text-sm"></i>
                                View Schema
                            </button>
                        </div>
                    </div>

                    <div class="border-t border-echoes-border-light">
                        <!-- Seller Message -->
                        <div class="p-3">
                            <h3 class="font-semibold text-white">Seller Message</h3>
                            <p class="text-sm text-white line-clamp-[4]">{{ rfp.seller_message }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Schema Modal -->
    <div v-if="showSchemaModal" class="fixed inset-0 bg-echoes-background-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-echoes-background-gray rounded-[13px] w-[90%] max-w-4xl max-h-[80vh] relative shadow-xl border border-echoes-border-light overflow-hidden">
            <div class="px-4 py-3 border-b border-echoes-border-light flex items-center justify-between">
                <h2 class="text-base font-semibold text-white">Schema Details</h2>
                <button 
                    @click="showSchemaModal = false"
                    class="inline-flex items-center justify-center w-7 h-7 rounded text-white/80 hover:text-echoes-primary hover:bg-echoes-background-black/50 transition-colors"
                >
                    <i class="mdi mdi-close text-lg"></i>
                </button>
            </div>
            <div class="p-4 overflow-y-auto custom-scrollbar" style="max-height: calc(80vh - 3.5rem);">
                <SchemaTable :schema="selectedRFP?.dataset?.schema" />
            </div>
        </div>
    </div>
</template>

<style scoped lang="postcss">
.loading-spinner {
  @apply w-8 h-8 border-4 border-echoes-border-gray border-t-echoes-primary rounded-full animate-spin;
}

.highlight-rfp {
  @apply ring-2 ring-orange-500 ring-opacity-75;
  animation: highlight-pulse 2s ease-in-out;
  transition: all 0.3s ease-in-out;
}

@keyframes highlight-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.7);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(249, 115, 22, 0.3);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(249, 115, 22, 0);
  }
}
</style>